// Content for Bhagavad-gītā 7.13 - How Do the Three Modes Affect Different Practitioners?
// Based on Śrīla Baladeva Vidyābhāṣuṇa's commentary on BG Chapter 7 (slides 57-82)

import type { TopicContent } from '../types';
import { THREE_MODES_SVG_MARKUP } from '../assets/3-modes-svg';

const content: TopicContent = {
    primarySloka: {
        reference: 'Bhagavad-gītā 7.13',
        sloka: `tribhir guṇamayair bhāvaiḥ
ebhiḥ sarvam idaṁ jagat |
mohitaṁ nābhijānāti
mām ebhyaḥ param avyayam ||`,
        translation:
            "Deluded (<strong>mohitam</strong>) by these three modes (<strong>tribhir guṇamayair bhāvaiḥ</strong>), all the world (<strong>sarvam idam jagat</strong>) does not know (<strong>nābhijānāti</strong>) Me, who am beyond these modes (<strong>mām ebhyaḥ param</strong>) and eternal (<strong>avyayam</strong>).",
    },
    blocks: [
        {
            type: 'diagram',
            diagram: {
                svgMarkup: THREE_MODES_SVG_MARKUP,
                zoomable: true,
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'I. Influence on Pure Śāstric Faith: Kevalā-bhakti (Beyond the Modes)',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 1.1.11',
                sloka: `anyābhilāṣitā-śūnyaṁ
jñāna-karmādy-anāvṛtam |
anukulyena kṛṣṇānu-
śīlanam bhaktir uttamā ||`,
                translation:
                    "The highest bhakti (<strong>bhaktir uttamā</strong>) is defined as continuous service or emotions (<strong>anuśīlanam</strong>) directed towards Kṛṣṇa, His expansion forms or others related to Him (<strong>kṛṣṇa</strong>), with a pleasing attitude towards Kṛṣṇa (<strong>anukulyena</strong>). It should be devoid of desires other than the desire to please the Lord (<strong>anyābhilāṣitā-śūnyam</strong>), and unobstructed by impersonal jñāna, the materialistic rituals of karma or other unfavorable acts (<strong>jñāna-karmādy-anāvṛtam</strong>).",
            },
        },
        {
            type: 'sub-heading',
            headingText: 'When Scriptural Faith is Contaminated by the Modes: Mixed Devotional Service',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.29.8',
                sloka: `abhisandhāya yo hiṁsāṁ
dambhaṁ mātsaryam eva vā
saṁrambhī bhinna-dṛg bhāvaṁ
mayi kuryāt sa tāmasaḥ`,
                translation:
                    "The angry person (<strong>saṁrambhī</strong>), devoid of compassion (<strong>bhinna-dṛk</strong>), who worships me (<strong>yah mayi bhāvaṁ kuryāt</strong>) with intentions (<strong>abhisandhāya</strong>) of violence, pride and hatred (<strong>hiṁsāṁ dambhaṁ mātsaryam</strong>), is tamasic in his bhakti (<strong>sa tāmasaḥ</strong>).",
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.29.9',
                sloka: `viṣayān abhisandhāya
yaśa aiśvaryam eva vā
arcādāv arcayed yo māṁ
pṛthag-bhāvaḥ sa rājasaḥ`,
                translation:
                    "He who worships me (<strong>yah māṁ arcayed</strong>) in the forms of deities and others (<strong>arca ādāu</strong>) with desire for other objects (<strong>pṛthag-bhāvaḥ abhisandhāya</strong>), with goals of material gain (<strong>viṣayān</strong>), fame (<strong>yaśa</strong>) or wealth (<strong>aiśvaryam</strong>) has rajasic bhakti (<strong>sa rājasaḥ</strong>).",
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 3.29.10',
                sloka: `karma-nirhāram uddiśya
parasmin vā tad-arpaṇam
yajed yañṭavyam iti vā
pṛthag-bhāvaḥ sa sāttvikaḥ`,
                translation:
                    "He who worships me (<strong>yajed</strong>) with a desire to destroy karma (<strong>karma-nirhāram uddiśya</strong>), making his works an offering to the Supreme Lord (<strong>parasmin tad-arpaṇam</strong>), or executing the worship as a duty (<strong>yañṭavyam iti</strong>), having a desire for liberation (<strong>pṛthag-bhāvaḥ</strong>), has sattvic bhakti (<strong>sa sāttvikaḥ</strong>).",
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'II. Influence on Svabhāva (Conditioned Nature): How the Modes Manifest as Behavioral Tendencies',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 14.6',
                sloka: `tatra sattvaṁ nirmalatvāt
prakāśakam anāmayam |
sukha-saṅgena badhnāti
jñāna-saṅgena cānagha ||`,
                translation:
                    "The mode of sattva (<strong>tatra sattvaṁ</strong>) is peaceful and gives knowledge (<strong>prakāśakam anāmayam</strong>), because of its purity (<strong>nirmalatvāt</strong>). It binds the jīva (<strong>badhnāti</strong>) with false conceptions of happiness and knowledge (<strong>sukha-saṅgena jñāna-saṅgena ca</strong>), O sinless one (<strong>anagha</strong>).",
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 14.7',
                sloka: `rajo rāgātmakaṁ viddhi
tṛṣṇā-saṅga-samudbhavam |
tan nibadhnāti kaunteya
karma-saṅgena dehinam ||`,
                translation:
                    "O son of Kunti (<strong>kaunteya</strong>), know that (<strong>viddhi</strong>) rajas, consisting of mutual attraction between men and women (<strong>rajo rāgātmakaṁ</strong>), and which gives rise to longing for the unattained and attachment to the attained (<strong>tṛṣṇā-saṅga-samudbhavam</strong>), binds the jīva (<strong>tad dehinam nibadhnāti</strong>) with attachment to action (<strong>karma-saṅgena</strong>).",
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 14.8',
                sloka: `tamas tv ajñāna-jaṁ viddhi
mohanaṁ sarva-dehinām |
pramādālasya-nidhrābhis
tan nibadhnāti bhārata ||`,
                translation:
                    "Know that (<strong>viddhi</strong>) tamas, which is produced from ignorance (<strong>tamas tv ajñāna-jaṁ</strong>), which causes confusion for all living beings in the form of opposite knowledge (<strong>mohanaṁ sarva-dehinām</strong>), binds the jīva (<strong>tan nibadhnāti</strong>) with inattention, laziness, and sleep (<strong>pramāda-ālasya-nidhrābhis</strong>).",
            },
        },
        {
            type: 'sub-heading',
            headingText: 'Five Disturbances in Devotional Practice',
        },
        {
            type: 'text',
            content:
                '<strong>1. Laya</strong> (sleep)<br/><strong>2. Vikṣepa</strong> (distraction)<br/><strong>3. Apratipatti</strong> (indifference or disinterest in spiritual topics)<br/><strong>4. Kaṣāya</strong> (tendency toward bad habits)<br/><strong>5. Rasāsvāda</strong> (taste for material enjoyment)',
        },
        {
            type: 'divider',
        },
        {
            type: 'heading',
            headingText: 'III. Influence on Sentimental Faith (Non-Scriptural Faith): Three Types of Worship Based on Previous Impressions',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 17.1',
                sloka: `arjuna uvāca
ye śāstra-vidhim utsṛjya
yajante śraddhayānvitāḥ
teṣāṁ niṣṭhā tu kā kṛṣṇa
sattvam āho rajas tamaḥ`,
                translation:
                    "Arjuna said: O Kṛṣṇa, what is the position of those (<strong>teṣāṁ niṣṭhā tu kā</strong>) who give up the rules of scripture (<strong>ye śāstra-vidhim utsṛjya</strong>) but worship with faith (<strong>yajante śraddhayānvitāḥ</strong>). Is this a sattva, or rajas and tamas condition (<strong>sattvam āho rajas tamaḥ</strong>)?",
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 17.2',
                sloka: `śrī-bhagavān uvāca
tri-vidhā bhavati śraddhā
dehinām sā svabhāva-jā |
sāttvikī rājasī caiva
tāmasī ceti tāṁ śṛṇu`,
                translation:
                    "The Lord said: The faith of embodied beings (<strong>dehinām śraddhā</strong>) is of three types (<strong>tri-vidhā bhavati</strong>), arising from their previous impressions (<strong>sā svabhāva-jā</strong>). It is sattvic, rajasic or tamasic in nature (<strong>sāttvikī rājasī caiva tāmasī ca iti</strong>). Hear about it (<strong>tāṁ śṛṇu</strong>).",
            },
        },
        {
            type: 'text',
            content:
                "<strong>Śrīla Baladeva Vidyābhāṣuṇa:</strong> Being asked, the Lord begins to speak. That faith by which one worships the devas and other beings while giving up rules of scripture because of laziness or difficulties, should be understood to arise from virtuous and sinful impressions from previous lives (svabhāva jā).",
        },
        {
            type: 'text',
            content:
                "That faith is of three types, arising from states of sattva, rajas and tamas, for the beginningless, unrelenting births of embodied souls in beginningless contact with the three guṇas of prakṛti.",
        },
        {
            type: 'text',
            content:
                "They do not have the spiritual intelligence arising from scripture taught by the devotees which will allow them to change those impressions (svabhāva). Therefore the three types of faith which arise from those impressions appear.",
        },
        {
            type: 'text',
            content:
                "Faith born from the scriptures is different. It brings about performance of actions only according to the scriptural directions (not because of impressions of the three guṇas).",
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 17.3',
                sloka: `sattvānurūpā sarvasya
śraddhā bhavati bhārata |
śraddhā-mayo 'yaṁ puruṣo
yo yac-chraddhaḥ sa eva saḥ ||`,
                translation:
                    "This faith corresponds to the nature of the internal organ (<strong>sattva anurūpā sarvasya śraddhā bhavati</strong>), O descendant of Bharata (<strong>bhārata</strong>). Man is made of his faith (<strong>śraddhā-mayo 'yaṁ puruṣaḥ</strong>). He becomes similar to whatever form he worships with faith (<strong>yo yat-chraddhaḥ sa eva saḥ</strong>).",
            },
        },
        {
            type: 'text',
            content:
                "<strong>Śrīla Baladeva Vidyābhāṣuṇa:</strong> Though faith itself is a function of sattva guṇa, it becomes three types of faith for the embodied soul possessing an internal organ whose very nature is to hold the impressions (svabhāva). The internal organ (antaḥkaraṇa) consists of the mind, intelligence, false ego and memory.",
        },
        {
            type: 'text',
            content:
                "The internal organ (sattvam) is composed of three guṇas, and corresponding to that, the faith of all living entities appears. The faith of the internal organ predominated by sattva guṇa is sattvic. The internal organ predominated by raja guṇa is rajasic. The internal organ predominated by tama guṇa is tamasic.",
        },
        {
            type: 'text',
            content:
                "The ordinary person (ayam puruṣaḥ), a worshipper who has an object of worship, endowed with one of three types of faith (śraddhamayaḥ), will be faithful to one of three respective types of object—devas, yakṣas or pretas (yat śraddhaḥ). That worshipper (sa) becomes endowed with the qualities of the deity worshipped (sa eva).",
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 17.4',
                sloka: `yajante sāttvikā devān
yakṣa-rakṣāṁsi rājasāḥ |
pretān bhūta-gaṇāṁś cānye
yajante tāmasā janāḥ ||`,
                translation:
                    "Those with sattvic nature worship the devas (<strong>yajante sāttvikā devān</strong>). Those with rajasic nature worship the Yakṣas and Rākṣasas (<strong>yakṣa-rakṣāṁsi rājasāḥ</strong>). Those with tamasic nature worship the Pretas and Bhūtas (<strong>pretān bhūta-gaṇāṁś ca anye yajante tāmasā janāḥ</strong>).",
            },
        },
        {
            type: 'text',
            content:
                "<strong>Śrīla Baladeva Vidyābhāṣuṇa:</strong> The results of worshipping with the different faiths are described in this verse. Those persons who with faith born of their previous impressions worship sattvika devas such as Vasu and Rudra without the discrimination of scripture are called sāttvika. Those who worship rajasic entities such as Yakṣas, Rākṣasas, Kuvera and Nairṛti are in the mode of passion. Those who worship tamasic entities such as pretas and bhūtas are in the mode of ignorance.",
        },
        {
            type: 'text',
            content:
                "The pretas are fallen brāhmaṇas who attain an airy body after death in the form of ulāmukhas, kaṭas or pūtanas, according to Manu. The pretas are a type of piśāca (evil spirit) according to others.",
        },
        {
            type: 'text',
            content:
                "Thus the three types of faith arising from the differing natures due to previous impressions, without following the rules of scripture because of laziness, have been described. It should be understood that by conquering over their natures by association with persons following Vedic principles, these persons sometimes come under the authority of the Vedas.",
        },
    ],
};

export default content;
